import { useEffect, useMemo, useState } from "react";
import {
  fetchCopyDriverRoutes,
  fetchDrivers,
  fetchUniqueDrivers,
} from "../../../redux/api/transport.api";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { clearAssignedDriversData } from "../../../redux/reducer/transport.reducer";
import ContentCard from "../../../components/misc/ContentCard";
import Dropdown, { DropdownValue } from "../../../components/misc/Dropdown";
import CustomButton from "../../../components/CustomButton";
import SvgIcon from "../../../helpers/SvgIcon";
import { getDateAndTime } from "../../../utils/dateAndTime";
import { AssignedDriverDataInterface } from "../../../redux/interfaces/transport.interface";
import FormButtons from "../../../components/misc/FormButtons";
import {
  checkEndpointStatus,
  endpointStatus,
} from "../../../helpers/apiHelper";
import { useNavigate } from "react-router-dom";

function CopyDriverRoutes() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const {
    transport: {
      assignedDriverData,
      assignedDriversData: {
        data: { data: assignedDriversData },
        isLoading: isLoadingAssign,
      },
      uniqueDrivers: {
        data: { data: uniqueDrivers },
        isLoading: isLoadingUniqueDrivers,
      },
    },
  } = useAppSelector((state) => ({
    transport: state.transport,
  }));

  const [
    uniqueSelectedDriverDropdownValue,
    setUniqueSelectedDriverDropdownValue,
  ] = useState<DropdownValue | undefined | null>(null);

  const [driverRoutes, setDriverRoutes] = useState<
    AssignedDriverDataInterface[]
  >([]);

  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);

  useEffect(() => {
    if (assignedDriverData) {
      dispatch(
        fetchDrivers({
          page: 1,
          perPage: 999,
          nume: assignedDriverData.nume,
          prenume: assignedDriverData.prenume,
          type: 0,
        }),
      );
    }
  }, [dispatch, assignedDriverData]);

  useEffect(() => {
    if (
      !isLoadingAssign &&
      !isLoadingUniqueDrivers &&
      assignedDriversData.length > 0
    ) {
      setDriverRoutes(assignedDriversData);
    } else {
      setDriverRoutes([]);
    }
  }, [assignedDriversData, isLoadingAssign, isLoadingUniqueDrivers]);

  useEffect(() => {
    dispatch(
      fetchUniqueDrivers({
        perPage: 10000,
      }),
    );
    return () => {
      dispatch(clearAssignedDriversData());
    };
  }, [dispatch]);

  const handleDeleteRoute = (id: number) => {
    const updatedRoutes = driverRoutes.filter((route) => route.id !== id);
    setDriverRoutes(updatedRoutes);
  };

  const handleSubmit = async () => {
    const formattedRoutes = driverRoutes.map((route) => ({
      ...route,
      id_sofer: uniqueSelectedDriverDropdownValue?.id,
    }));

    setIsLoadingSubmit(true);

    const resultAction = await dispatch(fetchCopyDriverRoutes(formattedRoutes));

    if (
      checkEndpointStatus(resultAction, fetchCopyDriverRoutes) !==
      endpointStatus.pending
    ) {
      setIsLoadingSubmit(false);
    }

    if (
      checkEndpointStatus(resultAction, fetchCopyDriverRoutes) ===
      endpointStatus.fulfilled
    ) {
      navigate(-1);
    }
  };

  const driversNomenclaturesDropdownValues = useMemo(
    () =>
      uniqueDrivers?.map((item) => ({
        label: `${item.prenume} ${item.nume}`,
        id: item.id_user,
      })),
    [uniqueDrivers],
  );

  return (
    <ContentCard cardTitle="Copiaza rutele soferului">
      <Dropdown
        className="mb-4 me-2"
        title="Selecteaza sofer"
        data={driversNomenclaturesDropdownValues}
        singleValue={uniqueSelectedDriverDropdownValue}
        setSingleValue={(value) => {
          setUniqueSelectedDriverDropdownValue(value);
        }}
      />

      {driverRoutes.map((driver, _, drivers) => {
        const textRoute =
          driver.ruta ?? `${driver.nod_start} -> ${driver.nod_destinatie}`;

        return (
          <ContentCard
            key={driver.id_sofer_rute}
            cardTitle={textRoute}
            className="pt-1 pb-1"
            cardBodyClassName="d-flex flex-row justify-content-between align-items-center">
            <div className="d-flex align-items-center justify-content-center">
              <div className="d-flex flex-column justify-content-center  align-items-start">
                <p className="py-1 badge badge-light-info">
                  Start: {getDateAndTime(driver.sofer_data_start, "-")}
                </p>
                <p className="py-1 badge badge-light-info">
                  Stop: {getDateAndTime(driver.sofer_data_stop, "-")}
                </p>
                <p className="py-1 badge badge-light-info">
                  Masina: {driver.nrauto}
                </p>
              </div>
            </div>
            {drivers.length > 1 && (
              <CustomButton
                variant="contained"
                color="error"
                withConfirmationModal={{
                  modalTitle: `Doriti sa stergeti ruta ${textRoute}?`,
                  modalLeftButtonColor: "inherit",
                  modalRightButtonOnClick: () => handleDeleteRoute(driver.id),
                  modalRightButtonText: "Sterge",
                  modalLeftButtonText: "Anuleaza",
                }}>
                <SvgIcon type="DELETE" />
              </CustomButton>
            )}
          </ContentCard>
        );
      })}

      <FormButtons
        goBackLabel="Anuleaza"
        goNextLabel="Salveaza"
        nextButtonDisabled={
          isLoadingUniqueDrivers ||
          isLoadingAssign ||
          !uniqueSelectedDriverDropdownValue ||
          driverRoutes.length === 0
        }
        nextButtonLoading={isLoadingSubmit}
        handleGoNext={handleSubmit}
      />
    </ContentCard>
  );
}

export default CopyDriverRoutes;
